import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getSmsConsentDisclosure, getAgentComplianceUrls } from '../../../lib/constants/ai-compliance-templates.js';

function AgentContactPage() {
  const { agentId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', smsConsent: false });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const appBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const complianceUrls = getAgentComplianceUrls(agentId, appBaseUrl);

  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    let cancelled = false;
    fetch(`/api/p/public-profile?agentId=${agentId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled) setProfile(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentId]);

  const brandName = profile?.businessName || profile?.fullName || 'Your Agent';
  const tagline = profile?.tagline || `${brandName} provides professional real estate guidance.`;
  const consentText = getSmsConsentDisclosure(brandName);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name?.trim() || !form.phone?.trim()) return;
    setSubmitting(true);
    try {
      await fetch('/api/p/contact-optin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, ...form }),
      });
    } catch (_) {}
    setSubmitted(true);
    setSubmitting(false);
  }

  const canSubmit = Boolean(form.name?.trim() && form.phone?.trim() && !submitting);

  return (
    <>
      <Helmet>
        <title>Contact {brandName}</title>
        <meta name="description" content={`Connect with ${brandName} for real estate guidance.`} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center justify-center px-4 py-14">
        <div className="w-full max-w-md bg-gray-900 rounded-2xl shadow-xl p-8 border border-gray-800">
          {loading ? (
            <p className="text-gray-400 text-center">Loading…</p>
          ) : submitted ? (
            <div className="text-center">
              <div className="text-4xl mb-4">✓</div>
              <h2 className="text-xl font-semibold mb-2">Thanks, we'll be in touch!</h2>
              <p className="text-gray-400 text-sm">
                {brandName} will reach out to you shortly.
                {form.smsConsent ? ' Reply STOP to any message to opt out.' : ''}
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-1 text-center">{brandName}</h1>
              <p className="text-gray-400 text-sm text-center mb-2">{tagline}</p>
              <p className="text-gray-500 text-xs text-center mb-7">
                Licensed real estate professional · Powered by Nurture Buddy.ai
              </p>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="name">
                    Full Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Jane Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="email">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="jane@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="phone">
                    Mobile Phone Number *
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="(555) 000-0000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Phone used to contact you. SMS sent only if optional box below is checked.
                  </p>
                </div>

                <div className="flex items-start gap-3 rounded-lg bg-gray-800/60 border border-gray-700 p-3">
                  <input
                    id="smsConsent"
                    name="smsConsent"
                    type="checkbox"
                    checked={form.smsConsent}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-600 bg-gray-700 accent-emerald-500 cursor-pointer"
                  />
                  <label htmlFor="smsConsent" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                    {consentText}
                    {' '}View{' '}
                    <a href={complianceUrls.privacyPolicyUrl || `#`} className="underline hover:text-gray-300">Privacy Policy</a>
                    {' '}and{' '}
                    <a href={complianceUrls.termsAndConditionsUrl || `#`} className="underline hover:text-gray-300">Terms and Conditions</a>.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm transition-colors"
                >
                  {submitting ? 'Sending…' : 'Connect with Me'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Messaging powered by{' '}
                  <a href="https://www.nurturebuddy.ai" className="underline hover:text-gray-300" target="_blank" rel="noreferrer">
                    Nurture Buddy.ai
                  </a>.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AgentContactPage;
